import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface SanitizationConfig {
  allowedTags?: string[];
  allowedAttributes?: { [tag: string]: string[] };
  allowedProtocols?: string[];
  maxLength?: number;
}

@Injectable({
  providedIn: 'root'
})
export class HtmlSanitizerService {
  private sanitizer = inject(DomSanitizer);

  // Default safe configuration for content
  private readonly defaultConfig: SanitizationConfig = {
    allowedTags: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'span', 'div'
    ],
    allowedAttributes: {
      'span': ['class'],
      'div': ['class'],
      'p': ['class'],
      'code': ['class'],
      'pre': ['class']
    },
    allowedProtocols: ['https:', 'mailto:'],
    maxLength: 10000
  };

  /**
   * Sanitize HTML content with strict filtering
   */
  sanitizeHtml(content: string, config?: Partial<SanitizationConfig>): SafeHtml {
    if (!content || typeof content !== 'string') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const finalConfig = { ...this.defaultConfig, ...config };
    
    // Length validation
    if (finalConfig.maxLength && content.length > finalConfig.maxLength) {
      content = content.substring(0, finalConfig.maxLength) + '...';
    }

    // Basic sanitization - remove script tags and dangerous attributes
    let sanitized = this.removeScriptTags(content);
    sanitized = this.removeDangerousAttributes(sanitized);
    sanitized = this.removeJavaScriptProtocols(sanitized);
    sanitized = this.filterAllowedTags(sanitized, finalConfig.allowedTags || []);
    sanitized = this.filterAllowedAttributes(sanitized, finalConfig.allowedAttributes || {});
    
    // Use Angular's built-in sanitizer as final step
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  /**
   * Sanitize text content (strips all HTML)
   */
  sanitizeText(content: string): string {
    if (!content || typeof content !== 'string') {
      return '';
    }

    // Remove all HTML tags
    return content.replace(/<[^>]*>/g, '');
  }

  /**
   * Sanitize content for display in forms (very restrictive)
   */
  sanitizeForInput(content: string): string {
    if (!content || typeof content !== 'string') {
      return '';
    }

    // Only allow basic text formatting, remove everything else
    return content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
      .replace(/javascript:/gi, '') // Remove javascript: protocols
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .trim();
  }

  private removeScriptTags(content: string): string {
    // Remove script tags and their content
    return content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }

  private removeDangerousAttributes(content: string): string {
    // Remove dangerous attributes like onclick, onload, etc.
    return content.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  }

  private removeJavaScriptProtocols(content: string): string {
    // Remove javascript: and data: protocols from href and src attributes
    return content
      .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, '')
      .replace(/src\s*=\s*["']javascript:[^"']*["']/gi, '')
      .replace(/href\s*=\s*["']data:[^"']*["']/gi, '')
      .replace(/src\s*=\s*["']data:[^"']*["']/gi, '');
  }

  private filterAllowedTags(content: string, allowedTags: string[]): string {
    if (allowedTags.length === 0) {
      return content.replace(/<[^>]*>/g, ''); // Remove all tags
    }

    // Create regex pattern for allowed tags
    const allowedPattern = allowedTags.join('|');
    const regex = new RegExp(`<(?!\/?(?:${allowedPattern})(?:\s|>))[^>]*>`, 'gi');
    
    return content.replace(regex, '');
  }

  private filterAllowedAttributes(content: string, allowedAttributes: { [tag: string]: string[] }): string {
    // This is a simplified implementation
    // In a production environment, you might want to use a more robust HTML parser
    return content.replace(/<(\w+)([^>]*?)>/gi, (match, tagName, attributes) => {
      const allowedAttrs = allowedAttributes[tagName.toLowerCase()] || [];
      
      if (allowedAttrs.length === 0) {
        return `<${tagName}>`;
      }

      // Filter attributes (simplified - in production, use proper HTML parser)
      const filteredAttrs = attributes.replace(/\s*(\w+)\s*=\s*["'][^"']*["']/g, (attrMatch: string, attrName: string) => {
        return allowedAttrs.includes(attrName.toLowerCase()) ? attrMatch : '';
      });

      return `<${tagName}${filteredAttrs}>`;
    });
  }

  /**
   * Validate if content is safe (returns boolean)
   */
  isContentSafe(content: string): boolean {
    if (!content || typeof content !== 'string') {
      return true;
    }

    // Check for dangerous patterns
    const dangerousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /<form/i,
      /<input/i,
      /data:/i
    ];

    return !dangerousPatterns.some(pattern => pattern.test(content));
  }
}